import {meService} from "@/src/modules/core/services/me";
import type {GetListResponse, ParamsOptions} from "@/src/modules/core/types";

import {safelistingsApi} from "../api";
import type {
  CorrectSafelistingRequest,
  CreateSafelistingRequest,
  ModifyDaysRequest,
  Safelisting,
  UpdateSafelistingRequest,
} from "../types";
import type {ResidentHomeSafelistingsAccountability} from "../types/resident-home-accountability";
import type {
  SafelistingAvailability,
  SafelistingAvailabilityParams,
} from "../types/safelisting-availability";

class SafelistingsService {
  async getSafelistings(options: ParamsOptions): Promise<GetListResponse<Safelisting>> {
    const {data} = await safelistingsApi.getSafelistings(options);
    return {data: data.data, meta: data.meta};
  }

  async getSafelisting(id: number): Promise<Safelisting> {
    const {data} = await safelistingsApi.getSafelisting(id);
    return data.data;
  }

  async createSafelisting(payload: CreateSafelistingRequest): Promise<Safelisting> {
    const {data} = await safelistingsApi.createSafelisting(payload);
    return data.data;
  }

  async updateSafelisting(id: number, payload: UpdateSafelistingRequest): Promise<Safelisting> {
    const {data} = await safelistingsApi.updateSafelisting(id, payload);
    return data.data;
  }

  async deleteSafelisting(id: number): Promise<void> {
    await safelistingsApi.deleteSafelisting(id);
  }

  async cancelSafelisting(id: number): Promise<Safelisting> {
    const {data} = await safelistingsApi.cancelSafelisting(id);
    return data.data;
  }

  async correctSafelisting(id: number, payload: CorrectSafelistingRequest): Promise<Safelisting> {
    const {data} = await safelistingsApi.correctSafelisting(id, payload);
    return data.data;
  }

  async modifyDays(id: number, payload: ModifyDaysRequest): Promise<Safelisting> {
    const {data} = await safelistingsApi.modifyDays(id, payload);
    return data.data;
  }

  async getQrCode(id: number): Promise<unknown> {
    const {data} = await safelistingsApi.getQrCode(id);
    return data.data;
  }

  async getAvailability(params: SafelistingAvailabilityParams): Promise<SafelistingAvailability> {
    const {data} = await safelistingsApi.getAvailability(params);
    return data.data;
  }

  async getAccountability(options?: ParamsOptions): Promise<unknown> {
    const {data} = await safelistingsApi.getAccountability(options);
    return data.data;
  }

  /** Resident home quota — scoped to selected property + household; no vehicle_id/plate. */
  async getResidentHomeAccountability(
    householdId: number,
    contextPropertyId: number,
  ): Promise<ResidentHomeSafelistingsAccountability> {
    const {data} = await safelistingsApi.getAccountability(undefined, {
      household_id: householdId,
      context_property_id: contextPropertyId,
    });
    return data.data as ResidentHomeSafelistingsAccountability;
  }

  async getHistory(options?: ParamsOptions): Promise<unknown> {
    const {data} = await safelistingsApi.getHistory(options);
    return data.data;
  }

  getIsCreateEnabled(): boolean {
    return meService.getHasPermission("safelisting store");
  }

  getIsUpdateEnabled(): boolean {
    return meService.getHasPermission("safelisting update");
  }

  getIsDeleteEnabled(): boolean {
    return meService.getHasPermission("safelisting delete");
  }

  getIsCancelEnabled(): boolean {
    return meService.getHasPermission("safelisting cancel");
  }

  getIsModifyDaysEnabled(): boolean {
    return meService.getHasPermission("safelisting modify days");
  }

  /** Optional policy action; aligns with Laravel `safelisting / show`. */
  getIsShowEnabled(): boolean {
    return meService.getHasPermission("safelisting show");
  }

  getIsCorrectEnabled(): boolean {
    return meService.getHasPermission("safelisting correct");
  }
}

export const safelistingsService = new SafelistingsService();
